import { create } from "zustand";
import { persist } from "zustand/middleware";
import { config } from "@/lib/config";
import { AUTH_COOKIE_NAME, STORAGE_KEYS } from "@/lib/constants";

// ---------------------------------------------------------------------------
// Cookie helpers – keep the `access_token` cookie in sync so that the Next.js
// Edge middleware (src/middleware.ts) can gate protected routes server-side.
// The cookie is NOT HttpOnly because we set it from client JS; the actual
// auth token validation still happens on the API server.
// ---------------------------------------------------------------------------

function setAuthCookie(token: string): void {
  if (typeof document === "undefined") return;
  // SameSite=Lax is the sensible default for a session cookie that needs to
  // survive top-level navigations.  `path=/` ensures every route can read it.
  // We intentionally omit `Secure` so it works on localhost during dev;
  // in production the cookie will be sent over HTTPS regardless.
  document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(token)}; path=/; SameSite=Lax`;
}

function clearAuthCookie(): void {
  if (typeof document === "undefined") return;
  // Expire the cookie immediately by setting max-age=0
  document.cookie = `${AUTH_COOKIE_NAME}=; path=/; SameSite=Lax; max-age=0`;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  app_role: string;
  team_role: string;
  bar_number?: string;
  bar_state?: string;
  tenant_id: string;
  requires_2fa: boolean;
}

export interface RegisterData {
  organization_name: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  bar_number?: string;
  bar_state?: string;
  app_role?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  setUser: (user: User) => void;
  setTokens: (access: string, refresh: string) => void;
  fetchUser: () => Promise<void>;
}

const API_BASE_URL = config.apiUrl;

async function apiRequest<T>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    token?: string | null;
  } = {},
): Promise<T> {
  const { method = "GET", body, token } = options;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const message =
      (data as Record<string, string> | null)?.detail ||
      (data as Record<string, string> | null)?.message ||
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const data = await apiRequest<{
            access_token: string;
            refresh_token: string;
            token_type: string;
            expires_in: number;
            requires_2fa: boolean;
          }>("/auth/login", {
            method: "POST",
            body: { email, password },
          });

          set({
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            isAuthenticated: true,
          });

          if (typeof window !== "undefined") {
            localStorage.setItem(STORAGE_KEYS.LEGACY_TOKEN, data.access_token);
          }
          setAuthCookie(data.access_token);

          // Fetch user profile after login
          try {
            const user = await apiRequest<User>("/auth/me", {
              token: data.access_token,
            });
            set({ user, isLoading: false });
          } catch {
            set({ isLoading: false });
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true });
        try {
          const result = await apiRequest<{
            access_token: string;
            refresh_token: string;
            token_type: string;
            expires_in: number;
            requires_2fa: boolean;
          }>("/auth/register", {
            method: "POST",
            body: data,
          });

          set({
            accessToken: result.access_token,
            refreshToken: result.refresh_token,
            isAuthenticated: true,
          });

          if (typeof window !== "undefined") {
            localStorage.setItem(STORAGE_KEYS.LEGACY_TOKEN, result.access_token);
          }
          setAuthCookie(result.access_token);

          // Fetch user profile after registration
          try {
            const user = await apiRequest<User>("/auth/me", {
              token: result.access_token,
            });
            set({ user, isLoading: false });
          } catch {
            // If /me fails, still consider registered
            set({ isLoading: false });
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
        if (typeof window !== "undefined") {
          localStorage.removeItem(STORAGE_KEYS.LEGACY_TOKEN);
        }
        // Clear cookie so middleware redirects to login
        clearAuthCookie();
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          get().logout();
          return;
        }

        try {
          const data = await apiRequest<{
            access_token: string;
            refresh_token: string;
          }>("/auth/refresh", {
            method: "POST",
            body: { refresh_token: refreshToken },
          });

          set({
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
          });

          if (typeof window !== "undefined") {
            localStorage.setItem(STORAGE_KEYS.LEGACY_TOKEN, data.access_token);
          }
          // Sync cookie for middleware route protection
          setAuthCookie(data.access_token);
        } catch {
          get().logout();
        }
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      setTokens: (access: string, refresh: string) => {
        set({ accessToken: access, refreshToken: refresh });
        if (typeof window !== "undefined") {
          localStorage.setItem(STORAGE_KEYS.LEGACY_TOKEN, access);
        }
        // Sync cookie for middleware route protection
        setAuthCookie(access);
      },

      fetchUser: async () => {
        const { accessToken } = get();
        if (!accessToken) return;

        try {
          const user = await apiRequest<User>("/auth/me", {
            method: "GET",
            token: accessToken,
          });
          set({ user, isAuthenticated: true });
        } catch {
          // Token might be expired, try refresh
          try {
            await get().refreshAccessToken();
            const newToken = get().accessToken;
            if (newToken) {
              const user = await apiRequest<User>("/auth/me", {
                method: "GET",
                token: newToken,
              });
              set({ user, isAuthenticated: true });
            }
          } catch {
            get().logout();
          }
        }
      },
    }),
    {
      name: STORAGE_KEYS.AUTH_STORE,
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
